<script lang="ts">
  // Die Befehlspalette (docs/shell.md).
  //
  // Bewusst KEINE zweite Suche: die gescorte Trefferliste samt Aliassen,
  // Fuzzy, gelerntem Ranking, Inventar (Sensoren, Aktoren, Tasks) und
  // der semantischen Suche ueber den Inferenzdienst steckt schon in NavSearchPanel.
  // Diese Datei ist nur die Huelle drumherum -- Overlay, Eingabefeld,
  // Tastatur, und bei leerer Eingabe der Verlauf.
  //
  // Der Unterschied zur TopBar-Suche ist der Ausgang: dort navigierte
  // ein Treffer die Seite, hier oeffnet er einen Tab.

  import AiIndicator from "../AiIndicator.svelte";
  import Icon from "../Icon.svelte";
  import NavSearchPanel from "../NavSearchPanel.svelte";
  import { t } from "../i18n.svelte";
  import { portal } from "../portal";
  import { sectionForPath } from "../nav-sections";
  import { seitenIcon, seitenTitel } from "./panels";
  import { zuletzt } from "./recent.svelte";
  import { oeffnePfad } from "./route-sync.svelte";
  import { shellGroups } from "./tabs.svelte";

  interface Props {
    onclose: () => void;
  }
  let { onclose }: Props = $props();

  let query = $state("");
  let active = $state(0);
  let treffer = $state(0);
  let feld = $state<HTMLInputElement | null>(null);

  // Was schon offen ist, gehoert nicht in den Verlauf: ein Sprung zu
  // etwas, das man vor sich hat, ist kein Angebot.
  const offenePfade = $derived(
    new Set(
      shellGroups()
        .flatMap((g) => g.tabs)
        .filter((tb) => tb.typ === "page")
        .map((tb) => String(tb.params.path ?? "")),
    ),
  );
  const verlauf = $derived(query.trim() === "" ? zuletzt(offenePfade) : []);

  // Die Zahl der Treffer kommt bei leerer Eingabe von uns, sonst von
  // NavSearchPanel -- die Pfeiltasten brauchen genau eine Wahrheit.
  $effect(() => {
    if (query.trim() === "") treffer = verlauf.length;
  });

  $effect(() => {
    feld?.focus();
  });

  function waehle(href: string) {
    onclose();
    oeffnePfad(href);
  }

  function taste(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onclose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      active = Math.min(active + 1, Math.max(0, treffer - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      active = Math.max(active - 1, 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Den aktiven Treffer anklicken statt seinen href zu erraten:
      // die Reihenfolge kennt die Liste, nicht wir.
      const el = document.querySelector<HTMLElement>(
        `[data-palette] [data-search-results] [data-idx="${active}"]`,
      );
      el?.click();
    }
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  use:portal
  class="hintergrund fixed inset-0 z-200 flex items-start justify-center"
  onclick={(e) => e.target === e.currentTarget && onclose()}
>
  <div
    class="palette surface border-border-strong w-full max-w-2xl rounded-xl border shadow-2xl"
    role="dialog"
    aria-modal="true"
    aria-label={t("shell.palette", "Suchen und springen")}
    data-palette
  >
    <!-- Das Lama links im Feld ist Symbol UND Zustand: es sagt beim
         Tippen, ob semantisch gesucht wird oder ob die Suche still auf
         Fuzzy zurueckgefallen ist. Es steht bewusst HIER und nicht in
         der Statusleiste -- der Zustand interessiert nur, waehrend man
         sucht, und die Leiste traegt nur, was immer gilt (docs/shell.md).
         Ein Klick fuehrt zur Inferenz-Seite: wer sieht dass es aus ist,
         will wissen warum. -->
    <div class="border-border flex items-center gap-2.5 border-b px-4">
      <AiIndicator size={16} onopen={() => waehle("/system/inferenz")} />
      <input
        bind:this={feld}
        bind:value={query}
        oninput={() => (active = 0)}
        onkeydown={taste}
        class="text-fg placeholder:text-fg-subtle w-full bg-transparent py-3 text-base outline-none"
        placeholder={t("shell.palette_hint", "Seite, Sensor, Container …")}
        autocomplete="off"
        spellcheck="false"
      />
    </div>

    <!-- data-search-results setzt der WIRT, nicht das Panel: die TopBar
         macht es genauso, und die Enter-Taste findet den aktiven Treffer
         nur darueber (die Listen vergeben bloss data-idx). -->
    <div class="max-h-[52vh] overflow-y-auto p-2" data-search-results>
      {#if query.trim() === ""}
        {#if verlauf.length > 0}
          <p class="t-eyebrow text-fg-subtle px-2 pb-1 pt-1">
            {t("shell.recent", "Zuletzt geöffnet")}
          </p>
          {#each verlauf as pfad, i (pfad)}
            {@const sec = sectionForPath(pfad)}
            <button
              type="button"
              class="eintrag"
              class:aktiv={i === active}
              data-idx={i}
              onclick={() => waehle(pfad)}
              onmouseenter={() => (active = i)}
            >
              <Icon name={seitenIcon(pfad)} size={14} />
              <span class="text-fg truncate">{seitenTitel(pfad)}</span>
              {#if sec}<span class="wo">{sec.label}</span>{/if}
            </button>
          {/each}
        {:else}
          <p class="text-fg-subtle t-meta px-2 py-3">
            {t("shell.palette_empty", "Tippen, um Seiten, Sensoren und Container zu finden.")}
          </p>
        {/if}
      {:else}
        <NavSearchPanel
          {query}
          {active}
          onSelect={waehle}
          onHover={(i) => (active = i)}
          onFilteredChange={(n) => (treffer = n)}
        />
      {/if}
    </div>

    <!-- Die Tastenzeile kam mit der TopBar-Pille weg. Sie gehoert an
         eine Palette, die man mit der Tastatur oeffnet: wer Strg P
         drueckt, will danach nicht zur Maus greifen. -->
    <div
      class="border-border text-fg-subtle t-meta flex shrink-0 items-center justify-between border-t px-4 py-2"
    >
      <span>↑ ↓ {t("cmdk.hint.navigate", "navigieren")}</span>
      <span>↵ {t("cmdk.hint.open", "öffnen")}</span>
      <span>Esc {t("cmdk.hint.close", "schließen")}</span>
    </div>
  </div>
</div>

<style>
  .hintergrund {
    background: color-mix(in srgb, #000 45%, transparent);
    padding-top: 12vh;
  }
  .palette {
    background: var(--bg-elev);
  }
  .eintrag {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    padding: 0.35rem 0.5rem;
    border-radius: 6px;
    color: var(--fg-muted);
    text-align: left;
    font-size: 0.85rem;
  }
  /* Auswahl neutral grau: in Akzentfarbe (orange) las sich der
     markierte Treffer wie eine Warnung (Tim 25.08.). */
  .eintrag.aktiv {
    background: color-mix(in srgb, var(--fg) 12%, transparent);
  }
  .wo {
    margin-left: auto;
    padding-left: 0.5rem;
    font-size: 0.72rem;
    color: var(--fg-subtle);
    white-space: nowrap;
  }
</style>
